import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import InputSettingsField from './InputSettingsField';
import Dropzone from '../../misc/Dropzone';
import Icon from '../../misc/Icon';
import FieldStyleSettings from './FieldStyleSettings';
import MetadataSettings from './MetadataSettings';
import ConditionalLogicSettings from './ConditionalLogicSettings';
import CheckboxSettingsField from './CheckboxSettingsField';
import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import SettingsLabel from './SettingsLabel';
import AccordionSettings from './AccordionSettings';
import { StyledAddonAfter, StyledAddonBefore, StyledFieldWrapper, StyledAccordionFieldWrapper, StyledButtonsWrapper, DividerLine } from './SettingsCommonElements';

import Button from '../../misc/Button';
import PageLayoutModes from '../../../constants/PageLayoutModes';
import FieldPresentationModes from '../../../constants/FieldPresentationModes';
import FileViews from '../../../constants/FileViews';
import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

const StyledWrapper = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
`;

const PageSettings = ({

  view,
  page,
  pageIndex,
  config,
  hidden,

  //Logic properties
  fileId,
  sortedPages,
  fields,

  //Action handlers
  onChange,
  onUpload,
  onUploadDelete,
  onDelete,

}) => {

  const theme = useContext(ThemeContext);

  const {
    _id,
    layout,
    presentation,
    cols,
    height,
    width,
    rowHeight,
    padding,
    metadata,
    logic
  } = page;

  const settingsNode = (
    <>
      <StyledFieldWrapper>
        <InputSettingsField
          value={page.name}
          type="text"
          onChange={(fieldId, value) => onChange(_id, { name: value })}
        />
      </StyledFieldWrapper>
      {!onUpload || view === FileViews.mobile ? null : (
        <>
          <StyledFieldWrapper>
            <Dropzone
              dragMessage="pdf, png, jpg, ..."
              onDrop={(files) => onUpload(_id, files[0])}
              accept={{
                'image/jpeg': ['.jpg', '.jpeg', '.JPG', '.JPEG'],
                'image/png': ['.png', '.PNG'],
                'application/pdf': ['.pdf', '.PDF']
              }}
            />
          </StyledFieldWrapper>
          {!page.backgroundImage ? null : (
            <StyledButtonsWrapper>
              <Button
                danger
                text="Remove Background"
                styles={{ marginBottom: '12px' }}
                icon={ThemeIcons.settings.delete}
                onClick={() => onUploadDelete(_id)}
              />
            </StyledButtonsWrapper>
          )}
        </>
      )}
      {sortedPages.length < 2 ? null : (
        <>
          <StyledFieldWrapper>
            <CheckboxSettingsField
              icon={ThemeIcons.settings.hide}
              iconColor={hidden ? theme.warningColor : theme.borderColor}
              id="hidden"
              label="Hidden"
              selected={hidden}
              onChange={(selected) => onChange(_id, { hidden: selected })}
            />
          </StyledFieldWrapper>
        </>
      )}
      {sortedPages.length < 2 ? null : (
        <>
          <DividerLine />
          <StyledFieldWrapper>
            <ConditionalLogicSettings
              fileId={fileId}
              targetLabel="page"
              targetPageId={page._id}
              sortedPages={sortedPages}
              fields={fields}
              logic={logic}
              onSubmit={(nextLogic) => onChange(_id, { logic: nextLogic })}
            />
          </StyledFieldWrapper>
        </>
      )}
      {!config?.metadata ? null : (
        <>
          <DividerLine />
          <MetadataSettings
            metadata={metadata}
            metadataOptions={config?.metadata}
            onChange={(changes) => {
              const nextMetadata = metadata ? { ...metadata, ...changes } : { ...changes };
              onChange(_id, { metadata: nextMetadata });
            }}
          />
        </>
      )}
      <SettingsLabel>Layout</SettingsLabel>
      <DividerLine style={{ marginBottom: 14 }} />
      <StyledFieldWrapper style={{ marginBottom: 6 }}>
        <FieldStyleSettings
          id="page-field"
          {...{

            paddingLabel: 'Padding',
            padding,
            onPaddingChange: (value) => onChange(_id, { padding: value }),

          }}
        />
      </StyledFieldWrapper>
      <StyledFieldWrapper>
        <InputSettingsField
          type="dropdown"
          value={presentation}
          addonAfter={<StyledAddonAfter>Field</StyledAddonAfter>}
          options={[
            {
              _id: FieldPresentationModes.normal,
              value: 'Normal',
            },
            {
              _id: FieldPresentationModes.transparent,
              value: 'Transparent',
            },
          ]}
          onChange={(i, value) => onChange(_id, { presentation: value })}
        />
      </StyledFieldWrapper>
      <StyledFieldWrapper>
        <InputSettingsField
          type="dropdown"
          value={layout}
          addonAfter={<StyledAddonAfter>Layout</StyledAddonAfter>}
          options={[
            {
              _id: PageLayoutModes.grid,
              value: 'Grid',
            },
            {
              _id: PageLayoutModes.float,
              value: 'Float',
            },
          ]}
          onChange={(i, value) => onChange(_id, { layout: value })}
        />
      </StyledFieldWrapper>
      <StyledFieldWrapper>
        <InputSettingsField
          id="grid-column-count"
          value={cols}
          addonBefore={<Icon icon={ThemeIcons.settings.pageColumns} />}
          addonAfter={<StyledAddonAfter>Columns</StyledAddonAfter>}
          type="number"
          onChange={(fieldId, value) => {
            if (value) onChange(_id, { cols: parseInt(value, 10) });
          }}
        />
      </StyledFieldWrapper>
      <StyledFieldWrapper>
        <InputSettingsField
          id="grid-row-height"
          value={rowHeight}
          addonBefore={<Icon icon={ThemeIcons.settings.pageRowHeight} />}
          addonAfter={<StyledAddonAfter>Row Height</StyledAddonAfter>}
          type="number"
          onChange={(fieldId, value) => {
            if (value) onChange(_id, { rowHeight: parseInt(value, 10) });
          }}
        />
      </StyledFieldWrapper>
      {!page.backgroundImage ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title="Advanced"
            height={100}
          >
            <StyledAccordionFieldWrapper>
              <InputSettingsField
                id="grid-column-count"
                value={height}
                type="number"
                addonBefore={<StyledAddonBefore>File Height</StyledAddonBefore>}
                addonAfter={<StyledAddonAfter>px</StyledAddonAfter>}
                onChange={(fieldId, value) => {
                  if (value) onChange(_id, { height: parseInt(value, 10) });
                }}
              />
            </StyledAccordionFieldWrapper>
            <StyledAccordionFieldWrapper>
              <InputSettingsField
                id="grid-column-count"
                value={width}
                addonBefore={<StyledAddonBefore>File Width</StyledAddonBefore>}
                type="number"
                addonAfter={<StyledAddonAfter>px</StyledAddonAfter>}
                onChange={(fieldId, value) => {
                  if (value) onChange(_id, { width: parseInt(value, 10) });
                }}
              />
            </StyledAccordionFieldWrapper>
          </AccordionSettings>
        </>
      )}
      <DividerLine />
      {!onDelete ? null : (
        <>
          <StyledButtonsWrapper>
            <Button
              danger
              icon={ThemeIcons.settings.delete}
              text="Delete"
              onClick={() => onDelete(page._id)}
            />
          </StyledButtonsWrapper>
        </>
      )}
    </>
  );

  return (
    <StyledWrapper>
      <FieldSettingsHeaderTitle
        icon={ThemeIcons.settings.page}
        title={`#${pageIndex + 1} Settings`}
      />
      {settingsNode}
    </StyledWrapper>
  );
};

PageSettings.propTypes = {
  page: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  onUpload: PropTypes.func,
  onDelete: PropTypes.func
};

export default PageSettings;
